import { useMemo } from "react";
import { usePayments } from "@/hooks/admin/useAdminData";
import type { PaymentStatus } from "@/mocks/admin/payments";

const statusMeta: Record<
  PaymentStatus,
  { label: string; hex: string; cls: string; icon: string }
> = {
  succeeded: {
    label: "Succeeded",
    hex: "#10B981",
    cls: "bg-emerald-500",
    icon: "ri-checkbox-circle-line",
  },
  pending: {
    label: "Pending",
    hex: "#F59E0B",
    cls: "bg-amber-500",
    icon: "ri-time-line",
  },
  failed: {
    label: "Failed",
    hex: "#EF4444",
    cls: "bg-rose-500",
    icon: "ri-close-circle-line",
  },
  refunded: {
    label: "Refunded",
    hex: "#64748B",
    cls: "bg-ink-500",
    icon: "ri-arrow-go-back-line",
  },
};

// Synthesized daily volume sparkline for current week
const dailyVolume = [
  { day: "Mon", value: 18400 },
  { day: "Tue", value: 22100 },
  { day: "Wed", value: 15800 },
  { day: "Thu", value: 26400 },
  { day: "Fri", value: 31200 },
  { day: "Sat", value: 12600 },
  { day: "Sun", value: 4250 },
];

export default function StatusMix() {
  const { data: payments } = usePayments();

  const stats = useMemo(() => {
    const counts: Record<string, { count: number; total: number }> = {};
    payments.forEach((p) => {
      if (!counts[p.status]) counts[p.status] = { count: 0, total: 0 };
      counts[p.status].count += 1;
      counts[p.status].total += p.amount;
    });
    const order: PaymentStatus[] = ["succeeded", "pending", "failed", "refunded"];
    return order
      .filter((k) => counts[k])
      .map((k) => ({
        key: k,
        ...statusMeta[k],
        count: counts[k].count,
        total: counts[k].total,
      }));
  }, [payments]);

  const totalCount = stats.reduce((s, x) => s + x.count, 0) || 1;

  // Daily volume sparkline
  const maxDaily = Math.max(...dailyVolume.map((d) => d.value));
  const weekTotal = dailyVolume.reduce((s, d) => s + d.value, 0);
  const todayValue = dailyVolume[dailyVolume.length - 2].value; // pretend today is Fri

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
      {/* Status mix */}
      <div className="bg-white border border-ink-100 rounded-lg p-6">
        <div className="flex items-start justify-between mb-1">
          <div>
            <h3 className="font-display text-lg font-bold text-ink-900">
              Transaction Status
            </h3>
            <p className="text-xs text-ink-500 mt-0.5">
              Mix across {totalCount} payments
            </p>
          </div>
        </div>

        {/* Horizontal stacked bar */}
        <div className="w-full h-4 rounded-full overflow-hidden flex mt-5 mb-4">
          {stats.map((s) => (
            <div
              key={s.key}
              className={`${s.cls} h-full transition-all hover:opacity-80`}
              style={{ width: `${(s.count / totalCount) * 100}%` }}
              title={`${s.label}: ${s.count}`}
            ></div>
          ))}
        </div>

        <ul className="space-y-2">
          {stats.map((s) => {
            const pct = (s.count / totalCount) * 100;
            return (
              <li key={s.key} className="flex items-center gap-2.5">
                <span className={`w-7 h-7 rounded-md ${s.cls} text-white flex items-center justify-center shrink-0`}>
                  <i className={`${s.icon} text-sm`}></i>
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-xs font-bold text-ink-900">{s.label}</span>
                    <span className="text-[11px] text-ink-500 tabular-nums">
                      <span className="font-bold text-ink-900">{s.count}</span> · ${s.total.toLocaleString()}
                    </span>
                  </div>
                  <div className="w-full h-1 rounded-full bg-cream-100 overflow-hidden mt-1">
                    <div
                      className={`h-full ${s.cls} transition-all`}
                      style={{ width: `${pct}%` }}
                    ></div>
                  </div>
                </div>
                <span className="text-xs font-bold tabular-nums text-ink-700 w-10 text-right">
                  {pct.toFixed(0)}%
                </span>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Daily volume */}
      <div className="bg-white border border-ink-100 rounded-lg p-6">
        <div className="flex items-start justify-between mb-1 flex-wrap gap-2">
          <div>
            <h3 className="font-display text-lg font-bold text-ink-900">
              Daily Volume
            </h3>
            <p className="text-xs text-ink-500 mt-0.5">This week, by day</p>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider font-bold text-ink-400">
              Week total
            </div>
            <div className="font-display text-lg font-bold text-ink-900 tabular-nums leading-tight">
              ${(weekTotal / 1000).toFixed(1)}k
            </div>
          </div>
        </div>

        {/* Today highlight */}
        <div className="bg-cream-50 border border-ink-100 rounded-md p-3 mt-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-ink-500">
              Friday (today)
            </div>
            <div className="font-display text-xl font-bold text-ink-900 tabular-nums leading-tight">
              ${todayValue.toLocaleString()}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] text-ink-500">vs. yesterday</div>
            <div className="text-xs font-bold text-emerald-600 inline-flex items-center gap-0.5">
              <i className="ri-arrow-up-line"></i>
              {(((todayValue - dailyVolume[3].value) / dailyVolume[3].value) * 100).toFixed(0)}%
            </div>
          </div>
        </div>

        {/* Bars */}
        <div className="flex items-end justify-between gap-2 mt-5 h-28">
          {dailyVolume.map((d, idx) => {
            const h = Math.max(4, (d.value / maxDaily) * 100);
            const isToday = idx === 4; // Fri
            return (
              <div
                key={d.day}
                className="flex-1 flex flex-col items-center gap-1.5 group cursor-pointer"
              >
                <div className="text-[10px] font-bold text-ink-700 opacity-0 group-hover:opacity-100 transition-opacity tabular-nums whitespace-nowrap">
                  ${(d.value / 1000).toFixed(1)}k
                </div>
                <div className="w-full flex-1 bg-cream-100 rounded-md overflow-hidden flex items-end">
                  <div
                    className={`w-full rounded-md transition-colors ${
                      isToday ? "bg-brand-500" : "bg-ink-800 group-hover:bg-ink-700"
                    }`}
                    style={{ height: `${h}%` }}
                  ></div>
                </div>
                <div
                  className={`text-[10px] font-semibold ${
                    isToday ? "text-brand-700 font-bold" : "text-ink-500"
                  }`}
                >
                  {d.day}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}